import { ChatOutlined } from '@mui/icons-material';
import { CuriosityNamespaceType } from '@procureai/curiosity-ui-mui';
import { Permission } from '@procureai/permissions';
import { DateFormatPreset, NumberFormatPreset } from '@procureai/ui-mui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { TsrctNegotiationCaseOrderField } from '@/generated/gql/graphql';
import {
  I18nNamespace,
  NegotiationCaseDataGridColumnGroup,
  NegotiationCaseDataGridField,
  NegotiationCaseDataGridRow,
} from '~/common/types';
import {
  CurrencyCell,
  DataGridConfig,
  DataGridLinkVariant,
  GridAlignmentType,
  GridSortDirectionType,
  LinkCell,
  LinkCellValue,
  NegotiationStatusChipCell,
  UseDataGridConfig,
  cellFactory,
  dateFormatterFactory,
  gridColDefFactory,
  gridColumnGroupFactory,
  headerGroupWithIcon,
  numberFormatterFactory,
  NegotiationProcessStatusChipCell,
  IssueChipCell,
  NumberCell,
  SupplierSourceReferenceCell,
  TooltipCell,
} from '~/components/DataGridCustom';
import { CuriosityUserSelectCell } from '~/components/DataGridCustom/cells/CuriosityUserSelectCell/CuriosityUserSelectCell';
import { CuriosityValueCell } from '~/components/DataGridCustom/cells/CuriosityValueCell/CuriosityValueCell';
import { IconCell } from '~/components/DataGridCustom/cells/IconCell/IconCell';
import { IconId } from '~/components/Icon';
import { LocationLink, LocationLinkProps } from '~/components/LocationLink/LocationLink';
import { RouteId } from '~/routes';
import { locationLabel } from '~/util/string';

export const useNegotiationCaseDataGridConfig: UseDataGridConfig<NegotiationCaseDataGridRow> = () => {
  const { t } = useTranslation();
  const { t: tColumn } = useTranslation(undefined, { keyPrefix: 'NegotiationCaseDataGrid.column' });
  const { t: tGroup } = useTranslation(undefined, { keyPrefix: 'NegotiationCaseDataGrid.group' });
  const { t: tCountries } = useTranslation(I18nNamespace.countries, {});
  const tLocation = useMemo(() => locationLabel(tCountries), [tCountries]);

  return useMemo(() => {
    const createColumn = gridColDefFactory({ tColumn, t });
    const createGroup = gridColumnGroupFactory({ tGroup });
    return {
      sortModel: [{ field: TsrctNegotiationCaseOrderField.sequenceNumber, sort: GridSortDirectionType.DESC }],
      columnVisibilityModel: {
        [NegotiationCaseDataGridField.NEGOTIATION_BOUNDARY_UPPER]: false,
        [NegotiationCaseDataGridField.NEGOTIATION_BOUNDARY_LOWER]: false,
        [NegotiationCaseDataGridField.SUPPLIER_LOCATION]: false,
        [NegotiationCaseDataGridField.SUPPLIER_EXTERNAL_REFERENCE]: false,
        [NegotiationCaseDataGridField.SUPPLIER_CONTACT_FIRST_NAME]: false,
        [NegotiationCaseDataGridField.SUPPLIER_CONTACT_LAST_NAME]: false,
        [NegotiationCaseDataGridField.SUPPLIER_CONTACT_PHONE]: false,
        [NegotiationCaseDataGridField.STATUS]: false,
        [NegotiationCaseDataGridField.UPDATED_AT]: false,
        [NegotiationCaseDataGridField.TOTAL_PRICE_DISCOUNT]: false,
        [NegotiationCaseDataGridField.TOTAL_DISCOUNTED_VALUE]: false,
        [NegotiationCaseDataGridField.DISCOUNT]: false,
        [NegotiationCaseDataGridField.HAS_SUPPLIER_COMMENT]: false,
        [NegotiationCaseDataGridField.DEMAND_SEQUENCE_NUMBER]: false,
        [NegotiationCaseDataGridField.ORGANIZATION_ID]: false,
        [NegotiationCaseDataGridField.LATEST_COMMENT]: false,
      },
      columns: [
        createColumn({
          field: NegotiationCaseDataGridField.SEQUENCE_NUMBER,
          renderCell: cellFactory(LinkCell),
          valueGetter: (value, row): LinkCellValue => ({
            variant: DataGridLinkVariant.PRIMARY,
            to: RouteId.negotiationDetail(row.id),
            label: value,
          }),
          valueFormatter: (value: { label: string }) => value?.label,
          minWidth: 100,
        }),
        createColumn({
          field: NegotiationCaseDataGridField.REFERENCE_ID,
          minWidth: 100,
        }),
        createColumn({
          field: NegotiationCaseDataGridField.DEMAND_SEQUENCE_NUMBER,
          renderCell: cellFactory(LinkCell),
          valueGetter: (value, row): LinkCellValue => ({
            variant: DataGridLinkVariant.PRIMARY,
            to: RouteId.demandDetail(row.demandId),
            label: value,
          }),
          valueFormatter: (value: { label: string }) => value?.label,
          minWidth: 100,
        }),

        createColumn({
          field: NegotiationCaseDataGridField.PROCESS_STATUS,
          renderCell: cellFactory(NegotiationProcessStatusChipCell, ({ row }) => ({
            processStatus: row.processStatus,
            status: row.status,
            cancellationReason: row.cancellationReason,
            cancellationReasonText: row.cancellationReasonText,
          })),
          valueGetter: (_value, row) => {
            if (!row.processStatus) return null;
            const label = t(`NegotiationProcessStatus.${row.processStatus}`);
            if (row.cancellationReason) {
              const reason = t(`CaseCancellationReason.${row.cancellationReason}`);
              const comment = row.cancellationReasonText ? ` — ${row.cancellationReasonText}` : '';
              return `${label} (${reason}${comment})`;
            }
            return label;
          },
          sortable: false,
          minWidth: 300,
        }),
        createColumn({
          field: NegotiationCaseDataGridField.HAS_SUPPLIER_COMMENT,
          renderCell: cellFactory(IconCell, ({ value }) => ({
            icon: value ? <ChatOutlined /> : undefined,
          })),
          minWidth: 150,
        }),
        createColumn({
          field: NegotiationCaseDataGridField.ORGANIZATION_ID,
          renderCell: cellFactory(CuriosityValueCell, {
            ns: CuriosityNamespaceType.ORGANIZATION,
          }),
          sortable: false,
          minWidth: 150,
        }),
        createColumn({
          field: NegotiationCaseDataGridField.CREATED_AT,
          valueFormatter: dateFormatterFactory({ preset: DateFormatPreset.TWO_DIGIT }),
          minWidth: 200,
        }),
        createColumn({
          field: NegotiationCaseDataGridField.SUPPLIER_NAME,
          renderCell: cellFactory(LinkCell, { needs: { permission: Permission.BaseSupplierList } }),
          valueGetter: (value, row): LinkCellValue => ({
            to: row.supplierId ? RouteId.supplierDetail(row.supplierId) : '',
            label: value,
          }),
          valueFormatter: (value: { label: string }) => value?.label,
          minWidth: 350,
        }),
        createColumn({
          field: NegotiationCaseDataGridField.SUPPLIER_CONTACT_EMAIL,
          minWidth: 250,
        }),
        createColumn({
          field: NegotiationCaseDataGridField.TOTAL_VALUE,
          renderCell: cellFactory(CurrencyCell, ({ row }) => ({
            isMasked: row.isMaskedBidding,
            currency: row.currency,
          })),
          minWidth: 200,
          align: GridAlignmentType.RIGHT,
        }),
        createColumn({
          field: NegotiationCaseDataGridField.RESPONSIBLE_IDS,
          renderCell: cellFactory(CuriosityUserSelectCell, { simplifyValue: true }),
          valueGetter: (value) => value && value[0],
          sortable: false,
          minWidth: 300,
        }),
        createColumn({
          field: NegotiationCaseDataGridField.TOTAL_DISCOUNTED_VALUE,
          renderCell: cellFactory(CurrencyCell, ({ row }) => ({
            isMasked: row.isMaskedBidding,
            currency: row.currency,
          })),
          minWidth: 200,
          align: GridAlignmentType.RIGHT,
        }),
        createColumn({
          field: NegotiationCaseDataGridField.TOTAL_PRICE_DISCOUNT,
          renderCell: cellFactory(CurrencyCell, ({ row }) => ({
            isMasked: row.isMaskedBidding,
            currency: row.currency,
          })),
          minWidth: 200,
          align: GridAlignmentType.RIGHT,
        }),
        createColumn({
          field: NegotiationCaseDataGridField.DISCOUNT,
          minWidth: 200,
          align: GridAlignmentType.RIGHT,
          renderCell: cellFactory(NumberCell, ({ row }) => ({
            isMasked: row.isMaskedBidding,
            preset: NumberFormatPreset.PERCENT_DECIMAL,
          })),
        }),
        createColumn({
          field: NegotiationCaseDataGridField.NEGOTIATION_BOUNDARY_UPPER,
          valueFormatter: numberFormatterFactory({ preset: NumberFormatPreset.PERCENT_DECIMAL }),
          minWidth: 200,
        }),
        createColumn({
          field: NegotiationCaseDataGridField.NEGOTIATION_BOUNDARY_LOWER,
          valueFormatter: numberFormatterFactory({ preset: NumberFormatPreset.PERCENT_DECIMAL }),
          minWidth: 200,
        }),
        createColumn({
          field: NegotiationCaseDataGridField.SUPPLIER_LOCATION,
          renderCell: cellFactory(LocationLink),
          valueGetter: (_value, row): LocationLinkProps['value'] => ({
            city: row.supplierCity,
            countryCode: row.supplierCountryCode,
            postalCode: row.supplierPostalCode,
            street: row.supplierAddress,
          }),
          valueFormatter: (value) => tLocation(value),
          minWidth: 600,
          sortable: false,
        }),
        createColumn({
          field: NegotiationCaseDataGridField.SUPPLIER_EXTERNAL_REFERENCE,
          renderCell: cellFactory(SupplierSourceReferenceCell, ({ row }) => ({
            supplierId: row.supplierId,
            supplierExternalReference: row.supplierExternalReference,
          })),
          sortable: false,
          minWidth: 200,
        }),
        createColumn({
          field: NegotiationCaseDataGridField.SUPPLIER_CONTACT_FIRST_NAME,
          minWidth: 200,
        }),
        createColumn({
          field: NegotiationCaseDataGridField.SUPPLIER_CONTACT_LAST_NAME,
          minWidth: 200,
        }),
        createColumn({
          field: NegotiationCaseDataGridField.SUPPLIER_CONTACT_PHONE,
          minWidth: 200,
        }),

        createColumn({
          field: NegotiationCaseDataGridField.UPDATED_AT,
          valueFormatter: dateFormatterFactory({ preset: DateFormatPreset.TWO_DIGIT }),
          minWidth: 200,
        }),
        createColumn({
          field: NegotiationCaseDataGridField.STATUS,
          renderCell: cellFactory(NegotiationStatusChipCell),
          minWidth: 400,
        }),
        createColumn({
          field: NegotiationCaseDataGridField.ISSUES_OPEN_COUNT,
          renderCell: cellFactory(IssueChipCell),
          minWidth: 150,
        }),
        createColumn({
          field: NegotiationCaseDataGridField.LATEST_COMMENT,
          minWidth: 250,
          renderCell: cellFactory(TooltipCell),
          valueGetter: (_value, row) => row.latestCommentMessage,
        }),
      ],
      columnGroupingModel: [
        createGroup({
          groupId: NegotiationCaseDataGridColumnGroup.NEGOTIATION,
          renderHeaderGroup: headerGroupWithIcon(IconId.negotiationCase),
          children: [
            //
            { field: NegotiationCaseDataGridField.SEQUENCE_NUMBER },
            { field: NegotiationCaseDataGridField.REFERENCE_ID },
            { field: NegotiationCaseDataGridField.PROCESS_STATUS },
            { field: NegotiationCaseDataGridField.DEMAND_SEQUENCE_NUMBER },
            { field: NegotiationCaseDataGridField.ORGANIZATION_ID },
            //
            { field: NegotiationCaseDataGridField.RESPONSIBLE_IDS },
            { field: NegotiationCaseDataGridField.TOTAL_VALUE },
            { field: NegotiationCaseDataGridField.TOTAL_PRICE_DISCOUNT },
            { field: NegotiationCaseDataGridField.DISCOUNT },
            { field: NegotiationCaseDataGridField.TOTAL_DISCOUNTED_VALUE },
            { field: NegotiationCaseDataGridField.NEGOTIATION_BOUNDARY_UPPER },
            { field: NegotiationCaseDataGridField.NEGOTIATION_BOUNDARY_LOWER },
            { field: NegotiationCaseDataGridField.UPDATED_AT },
            { field: NegotiationCaseDataGridField.CREATED_AT },
            { field: NegotiationCaseDataGridField.STATUS },
            { field: NegotiationCaseDataGridField.HAS_SUPPLIER_COMMENT },
          ],
        }),
        createGroup({
          groupId: NegotiationCaseDataGridColumnGroup.SUPPLIER,
          renderHeaderGroup: headerGroupWithIcon(IconId.supplier),
          children: [
            //
            { field: NegotiationCaseDataGridField.SUPPLIER_NAME },
            { field: NegotiationCaseDataGridField.SUPPLIER_LOCATION },
            { field: NegotiationCaseDataGridField.SUPPLIER_EXTERNAL_REFERENCE },
            { field: NegotiationCaseDataGridField.SUPPLIER_CONTACT_FIRST_NAME },
            { field: NegotiationCaseDataGridField.SUPPLIER_CONTACT_LAST_NAME },
            { field: NegotiationCaseDataGridField.SUPPLIER_CONTACT_EMAIL },
            { field: NegotiationCaseDataGridField.SUPPLIER_CONTACT_PHONE },
          ],
        }),
        createGroup({
          groupId: NegotiationCaseDataGridColumnGroup.ISSUES,
          renderHeaderGroup: headerGroupWithIcon(IconId.ErrorOutline),
          children: [
            //
            { field: NegotiationCaseDataGridField.ISSUES_OPEN_COUNT },
            { field: NegotiationCaseDataGridField.LATEST_COMMENT },
          ],
        }),
      ],
      exportModel: {
        [NegotiationCaseDataGridField.RESPONSIBLE_IDS]: {
          disable: true,
        },
        [NegotiationCaseDataGridField.ORGANIZATION_ID]: {
          resolveCuriosity: CuriosityNamespaceType.ORGANIZATION,
        },
      },
    } as DataGridConfig<NegotiationCaseDataGridRow>;
  }, [t, tLocation, tColumn, tGroup]);
};
